/**
 * Operate-UI client for the suggest-questions endpoint. Triggered
 * on-demand from the "Suggest questions" button under each chart
 * bubble; the LLM analyses the package / module schema (tables,
 * columns, relationships — never the actual data) and returns
 * 3-5 short follow-up questions the user can click to dispatch
 * through the narrate path.
 *
 * <p>The endpoint only knows how to fetch schema for
 * {@code cognos.package.run} and {@code cognos.dataModule.run}
 * sources today. For other source kinds the backend returns an
 * empty array + an {@code unavailableReason} the UI surfaces
 * inline ("suggestions unavailable for this source kind").
 */
import { AxiosError } from "axios";
import { api } from "./api";

export type SuggestedKind =
  | "trend"
  | "outlier"
  | "comparison"
  | "ranking"
  | "distribution"
  | "relationship"
  | "summary"
  | "other";

export interface SuggestedQuestion {
  text: string;
  kind: SuggestedKind;
}

export interface SuggestQuestionsResult {
  questions:            SuggestedQuestion[];
  schemaTables:         number;
  schemaRelationships:  number;
  /** Set when no questions are returned and the backend can name a reason. */
  unavailableReason?:   string;
}

export interface SuggestQuestionsApiError {
  code:   string;
  detail: string;
  status: number;
}

export interface SuggestSourceRef {
  kind:       string;
  searchPath: string;
}

/**
 * Ask the LLM to suggest follow-up questions for the package / module
 * the chart was rendered from. The endpoint validates output kinds
 * against an allow-list; unknown kinds are coerced to {@code "other"}
 * server-side so the chip-rendering switch can be exhaustive.
 */
export async function suggestQuestionsForSource(
  source:          SuggestSourceRef,
  renderedColumns: string[],
): Promise<SuggestQuestionsResult> {
  try {
    const { data } = await api.post<{
      questions?:           Array<{ text?: string; kind?: string }>;
      schemaTables?:        number;
      schemaRelationships?: number;
      unavailableReason?:   string;
    }>("/visualize/suggest-questions", {
      source,
      renderedColumns,
    });
    const questions: SuggestedQuestion[] = Array.isArray(data?.questions)
      ? data.questions
          .filter((q): q is { text: string; kind: string } =>
            !!q && typeof q.text === "string" && q.text.trim().length > 0)
          .map((q) => ({
            text: q.text,
            kind: normalizeKind(q.kind),
          }))
      : [];
    return {
      questions,
      schemaTables:        typeof data?.schemaTables        === "number" ? data.schemaTables        : 0,
      schemaRelationships: typeof data?.schemaRelationships === "number" ? data.schemaRelationships : 0,
      unavailableReason:   typeof data?.unavailableReason   === "string" ? data.unavailableReason   : undefined,
    };
  } catch (e) {
    if (e instanceof AxiosError) {
      const body = e.response?.data as { error?: string; detail?: string } | undefined;
      const err: SuggestQuestionsApiError = {
        code:   typeof body?.error  === "string" ? body.error  : "http.status",
        detail: typeof body?.detail === "string" ? body.detail : (e.message ?? `HTTP ${e.response?.status}`),
        status: e.response?.status ?? 0,
      };
      throw err;
    }
    throw e;
  }
}

function normalizeKind(raw: string | undefined): SuggestedKind {
  const allowed: SuggestedKind[] = [
    "trend", "outlier", "comparison", "ranking",
    "distribution", "relationship", "summary",
  ];
  const k = (raw ?? "").toLowerCase();
  return allowed.includes(k as SuggestedKind) ? (k as SuggestedKind) : "other";
}
